/** Wall-clock time a job took, or has taken so far. */
export function formatDuration(
  startedAt: string | null,
  completedAt: string | null,
  nowMs: number,
): string | null {
  if (!startedAt) return null;
  const from = Date.parse(startedAt);
  if (Number.isNaN(from)) return null;
  const to = completedAt ? Date.parse(completedAt) : nowMs;
  const secs = Math.max(0, Math.round((to - from) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

/** Coarse "how long ago" label for a run's enqueue time. */
export function formatRelative(iso: string | null, nowMs: number): string {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "—";
  const secs = Math.round((nowMs - then) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

/**
 * Chain position of a run. A run with no chain metadata is the first link of
 * its own chain — the runner defaults an unannotated job to link 1 — so it
 * reads as `Initial` rather than as unknown.
 */
export function chainLabel(link: number | null): string {
  return (link ?? 1) > 1 ? `Link ${link}` : "Initial";
}

/**
 * Orders object keys alphabetically, recursively.
 *
 * Array order is left alone: a counter map's key order is an accident of how
 * the runner incremented it, but a list's order is part of the record.
 */
function withSortedKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withSortedKeys);
  if (value === null || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, nested]) => [key, withSortedKeys(nested)]),
  );
}

/**
 * Pretty-prints a stored payload with its keys sorted, falling back to the raw
 * text if it is not JSON.
 */
export function formatJson(raw: string | null): string | null {
  if (!raw) return null;
  try {
    return JSON.stringify(withSortedKeys(JSON.parse(raw)), null, 2);
  } catch {
    return raw;
  }
}

/** Full timestamp for the history table, where exact ordering matters. */
export function formatAbsolute(iso: string | null): string {
  if (!iso) return "—";
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return "—";
  return new Date(parsed).toLocaleString();
}
